import { useEffect, useMemo } from "react";
import type { TypedDocumentNode } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { queries } from "@/graphql";
import { useSubscriptionsList } from "@/hooks/permissions";
import { enqueueSnackbar } from "notistack";
import { useUser } from "@/app/providers";
import { getRoleTypeNameFromKey, getRoleTypeNameFromUser } from "@/utils";
import type {
  QueryTenantsListArgs,
  SubscriptionPlanType,
  TenantType,
} from "@/types/graphql";

type UseTenantListProps = QueryTenantsListArgs & {
  activeOnly?: boolean;
  ignoreRole?: boolean;
};

type TenantListItem = TenantType & {
  subscriptionPlanName: string | null;
  usersCount: number;
  companiesCount: number;
};

interface TenantsListQueryResponse {
  tenantsList: Array<TenantType | null> | null;
}

const tenantsListQuery = queries.tenantsList as TypedDocumentNode<
  TenantsListQueryResponse,
  QueryTenantsListArgs
>;

/**
 * Custom hook to fetch the list of tenants.
 * @param {object} props - The properties to be passed to the query.
 * @returns {{tenantsList: Array<object>, loading: boolean}} - The list of tenants and the loading state.
 */
export function useTenantList(props: UseTenantListProps = {}) {
  const { user } = useUser();
  const roleType = getRoleTypeNameFromUser(user);

  const { activeOnly, ignoreRole, partnerId, tenantId, email } = props;

  const queryVariables = useMemo(() => {
    const vars: QueryTenantsListArgs = {};
    if (tenantId) vars.tenantId = tenantId;
    if (partnerId) {
      vars.partnerId = partnerId;
    } else if (roleType === "partner") {
      vars.partnerId = user?.currentPartnerId;
    }
    if (email) {
      vars.email = email;
    } else if (roleType === "company") {
      vars.email = user?.email;
    }
    return vars;
  }, [
    tenantId,
    partnerId,
    email,
    roleType,
    user?.currentPartnerId,
    user?.email,
  ]);

  const {
    data,
    loading: loadingTenants,
    error,
  } = useQuery(tenantsListQuery, {
    variables: queryVariables,
    errorPolicy: "all",
    skip: !roleType,
  });

  // Handle errors with snackbar
  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message, {
        variant: "error",
      });
    }
  }, [error]);

  const { subscriptionsList, loading: loadingSubscriptions } =
    useSubscriptionsList();

  const tenantsList = useMemo<TenantListItem[]>(() => {
    // If roleType is not available, return empty array
    if (!roleType) {
      return [];
    }

    let list: TenantListItem[] = (data?.tenantsList || [])
      .filter((tenant): tenant is TenantType => Boolean(tenant))
      .map((tenant) => {
        const subscription = subscriptionsList.find(
          (sub: SubscriptionPlanType) => sub.key === tenant.subscriptionPlan,
        );
        return {
          ...tenant,
          subscriptionPlanName: subscription ? subscription.name : null,
          usersCount: tenant.users?.length || 0,
          companiesCount: tenant.companies?.length || 0,
        };
      });

    // Filter by activeOnly if specified
    if (activeOnly) {
      const currentDate = new Date();
      list = list.filter((tenant) => {
        const isActive = tenant.status === "Active";
        const isNotExpired =
          !tenant.subscriptionExpireAt ||
          new Date(tenant.subscriptionExpireAt as string | number | Date) >=
            currentDate;
        return isActive && isNotExpired;
      });
    }

    // Filter tenants based on user role for company roleType (unless ignoreRole is true)
    if (!ignoreRole && roleType === "company" && user?.id) {
      list = list.filter((tenant) => {
        return tenant.users?.some(
          (tenantUser) =>
            tenantUser?.userId === user.id &&
            (!user?.currentRoleKey ||
              tenantUser?.role === user.currentRoleKey ||
              tenant.Id === user?.currentTenantId ||
              getRoleTypeNameFromKey(tenantUser?.role) === roleType),
        );
      });
    }

    return list;
  }, [
    roleType,
    data?.tenantsList,
    subscriptionsList,
    activeOnly,
    ignoreRole,
    user?.currentRoleKey,
    user?.currentTenantId,
    user?.id,
  ]);

  return {
    tenantsList,
    loading: loadingTenants || loadingSubscriptions,
  };
}
